import { z } from "zod"

export const NotificationLocaleSchema = z.enum(["ru", "en", "hy"])
export type NotificationLocale = z.infer<typeof NotificationLocaleSchema>

export const NotificationKindSchema = z.enum([
  "admin_message",
  "purchase_receipt",
  "post_purchase_follow_up",
  // Автотриггеры (Э5): движок эмитит их на реальные события платформы.
  "order_status",
  // Новый оплаченный заказ — магазину (17.09.2026). До него вся лента была
  // адресована покупателю, и продавец о продаже не узнавал.
  "order_new",
  // Итог модерации карточки товара — магазину (17.09.2026).
  "product_moderation",
  "request_offer",
  // Новый запрос покупателя в категории — продавцам категории (fd-request-20).
  "request_new",
  "return_status",
  "seller_payout",
  "new_device",
  "chat_message",
  "review_activity",
  // Соц-события карточки товара (Э6): новый вопрос продавцу и ответ покупателю.
  "product_question",
  "product_answer",
  // Центр помощи (Э5): ответ поддержки в обращении — автору обращения.
  "support_reply",
  // Уведомления v2 (fd-notif-01/06/07/12): товар снова в наличии, снижение
  // цены на избранное и еженедельный дайджест новинок/подборок.
  "waitlist_restock",
  "price_drop",
  "weekly_picks",
  // Онбординг продавца (Э7, fd-onboard-18/19/20): заявка отправлена на
  // доработку с замечанием — продавцу; продавец исправил и отправил повторно —
  // ответственному менеджеру.
  "application_rework",
  "application_resubmitted",
  // Итог модерации заявки — продавцу (Э7, fd-onboard-29). До них продавец
  // узнавал только о доработке: заявка принята, одобрена или отклонена —
  // об этом не сообщалось нигде.
  "application_moderation",
  "application_approved",
  "application_rejected",
  // Акции (П3, этап 8): старт акции — подписчику календаря (fd-promo-33),
  // решение по заявке на участие — магазину (fd-promo-32), приглашение в
  // коллаборацию — магазину (fd-promo-11). CHECK в базе — миграция 332.
  "promo_started",
  "promo_application",
  "promo_collab_invite",
  // Итог модерации рекламной кампании — магазину (этап 8, П1, fd-promo-29).
  "ad_moderation",
  // Доделка ТЗ 31.10.2026 (пакет C, миграция 520):
  //   seller_commission    — продавцу: сколько удержано за период (комиссия,
  //                          продвижение, штрафы) — fd-payment-06 / fd-notif-11;
  //   seller_weekly_digest — продавцу: аналитика недели (fd-notif-12);
  //   cart_price_drop      — покупателю: подешевел товар из корзины (fd-notif-07);
  //   review_reply         — покупателю: магазин ответил на отзыв (fd-notif-09);
  //   marketing_digest     — покупателю: сводка маркетинговых за день/неделю (fd-notif-14).
  "seller_commission",
  "seller_weekly_digest",
  "cart_price_drop",
  "review_reply",
  "marketing_digest",
])
export type NotificationKind = z.infer<typeof NotificationKindSchema>

export const NotificationCategorySchema = z.enum([
  "system",
  "trade",
  "marketing",
  "social",
  "financial",
  "analytics",
  // §9: категория «Мне нравится» — чип есть в фильтрах ленты и в настройках,
  // добавлена аддитивно (значение "system" сохранено ради веб-клиента).
  "likes",
])
export type NotificationCategory = z.infer<typeof NotificationCategorySchema>

export const NotificationActionSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("order"), orderId: z.string().min(1) }),
  z.object({ kind: z.literal("product"), productId: z.string().min(1) }),
  z.object({ kind: z.literal("shop"), shopSlug: z.string().min(1) }),
  z.object({ kind: z.literal("chat"), conversationId: z.string().min(1) }),
  z.object({ kind: z.literal("profile") }),
])
export type NotificationAction = z.infer<typeof NotificationActionSchema>

export const NotificationLocalizedTextSchema = z.object({
  title: z.string().trim().min(1).max(160),
  body: z.string().trim().min(1).max(5000),
})
export type NotificationLocalizedText = z.infer<typeof NotificationLocalizedTextSchema>

export const NotificationLocalizedContentSchema = z.object({
  ru: NotificationLocalizedTextSchema,
  en: NotificationLocalizedTextSchema.optional(),
  hy: NotificationLocalizedTextSchema.optional(),
})
export type NotificationLocalizedContent = z.infer<typeof NotificationLocalizedContentSchema>

export const resolveNotificationContent = (
  content: NotificationLocalizedContent,
  locale: NotificationLocale,
): NotificationLocalizedText => content[locale] ?? content.ru

export const NotificationItemSchema = z.object({
  id: z.string().min(1),
  campaignId: z.string().min(1),
  kind: NotificationKindSchema,
  category: NotificationCategorySchema,
  title: z.string(),
  body: z.string(),
  data: z.record(z.unknown()),
  action: NotificationActionSchema.optional(),
  readAt: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime(),
})
export type NotificationItem = z.infer<typeof NotificationItemSchema>

export const NotificationPageSchema = z.object({
  items: z.array(NotificationItemSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
})
export type NotificationPage = z.infer<typeof NotificationPageSchema>

export const NotificationUnreadCountSchema = z.object({ count: z.number().int().nonnegative() })
export type NotificationUnreadCount = z.infer<typeof NotificationUnreadCountSchema>

export const NotificationListQuerySchema = z.object({
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  // fd-notif-17: «read» — только прочитанные (витрина: «прочитанные / непрочитанные»).
  filter: z.enum(["all", "unread", "read"]).default("all"),
  // fd-notif-17: фильтр ленты по категории. Пусто — все категории.
  category: NotificationCategorySchema.optional(),
  // Веб-макет «Профиль»: фильтр по темам покупателя через запятую
  // (cart,orders,…; см. NotificationTopicSchema ниже). Пусто — все темы.
  topics: z.string().max(200).optional(),
  locale: NotificationLocaleSchema.default("ru"),
})
export type NotificationListQuery = z.infer<typeof NotificationListQuerySchema>

export const NotificationReadBySourceRequestSchema = z.object({
  kind: NotificationKindSchema,
  sourceId: z.string().min(1),
})
export type NotificationReadBySourceRequest = z.infer<typeof NotificationReadBySourceRequestSchema>

export const NotificationAudienceSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("individual"), userId: z.string().min(1) }),
  z.object({ kind: z.literal("all_users") }),
  z.object({ kind: z.literal("all_buyers") }),
  z.object({ kind: z.literal("all_sellers") }),
])
export type NotificationAudience = z.infer<typeof NotificationAudienceSchema>

export const NotificationCampaignStatusSchema = z.enum([
  "draft",
  "queued",
  "processing",
  "scheduled",
  "sent",
  "cancelled",
  "suppressed",
  "failed",
])
export type NotificationCampaignStatus = z.infer<typeof NotificationCampaignStatusSchema>

export const NotificationDeliveryStatsSchema = z.object({
  targeted: z.number().int().nonnegative(),
  materialized: z.number().int().nonnegative(),
  read: z.number().int().nonnegative(),
  unread: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
})
export type NotificationDeliveryStats = z.infer<typeof NotificationDeliveryStatsSchema>

/** Дневной срез прочтений кампании (МСК): сколько доставлено и прочитано за
 *  календарный день. Для графика «во времени» в отчёте по рассылке. */
export const NotificationCampaignStatsByDaySchema = z.object({
  day: z.string(), // YYYY-MM-DD в МСК
  delivered: z.number().int().nonnegative(),
  read: z.number().int().nonnegative(),
})
export type NotificationCampaignStatsByDay = z.infer<typeof NotificationCampaignStatsByDaySchema>

/** Статистика доставки/прочтений кампании — ответ
 *  GET /admin/notification-campaigns/:id/stats. */
export const NotificationCampaignStatsSchema = z.object({
  campaignId: z.string().min(1),
  // Целевая аудитория (recipient_count кампании).
  recipients: z.number().int().nonnegative(),
  // Материализованные доставки (строки в ленте получателей).
  delivered: z.number().int().nonnegative(),
  read: z.number().int().nonnegative(),
  // Доля прочтений от доставленных, 0..1.
  readRate: z.number().min(0).max(1),
  // Разбивка прочтений по дням (МСК) — по времени фактического прочтения.
  byTime: z.array(NotificationCampaignStatsByDaySchema),
})
export type NotificationCampaignStats = z.infer<typeof NotificationCampaignStatsSchema>

export const NotificationCampaignSchema = z.object({
  id: z.string().min(1),
  kind: NotificationKindSchema,
  category: NotificationCategorySchema,
  content: NotificationLocalizedContentSchema,
  data: z.record(z.unknown()),
  action: NotificationActionSchema.optional(),
  audience: NotificationAudienceSchema,
  status: NotificationCampaignStatusSchema,
  statusReason: z.string().optional(),
  availableAt: z.string().datetime(),
  createdByUserId: z.string().optional(),
  cancelledByUserId: z.string().optional(),
  stats: NotificationDeliveryStatsSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  sentAt: z.string().datetime().optional(),
  cancelledAt: z.string().datetime().optional(),
})
export type NotificationCampaign = z.infer<typeof NotificationCampaignSchema>

export const NotificationCampaignPageSchema = z.object({
  items: z.array(NotificationCampaignSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
})
export type NotificationCampaignPage = z.infer<typeof NotificationCampaignPageSchema>

export const NotificationCampaignDraftRequestSchema = z.object({
  content: NotificationLocalizedContentSchema,
  category: NotificationCategorySchema,
  audience: NotificationAudienceSchema,
  action: NotificationActionSchema.optional(),
  data: z.record(z.unknown()).default({}),
  availableAt: z.string().datetime().optional(),
})
export type NotificationCampaignDraftRequest = z.infer<
  typeof NotificationCampaignDraftRequestSchema
>

export const NotificationCampaignUpdateRequestSchema =
  NotificationCampaignDraftRequestSchema.partial()
export type NotificationCampaignUpdateRequest = z.infer<
  typeof NotificationCampaignUpdateRequestSchema
>

export const NotificationCampaignPreviewRequestSchema = z.object({
  locale: NotificationLocaleSchema.default("ru"),
})
export type NotificationCampaignPreviewRequest = z.infer<
  typeof NotificationCampaignPreviewRequestSchema
>

export const NotificationCampaignPreviewSchema = z.object({
  campaignId: z.string().min(1),
  previewToken: z.string().min(1),
  expiresAt: z.string().datetime(),
  eligibleCount: z.number().int().nonnegative(),
  sample: z.array(z.object({ id: z.string(), name: z.string(), email: z.string() })).max(10),
  content: NotificationLocalizedTextSchema,
})
export type NotificationCampaignPreview = z.infer<typeof NotificationCampaignPreviewSchema>

export const NotificationCampaignSendRequestSchema = z.object({
  previewToken: z.string().min(1),
  idempotencyKey: z.string().min(8).max(200),
  confirmation: z.string().optional(),
})
export type NotificationCampaignSendRequest = z.infer<typeof NotificationCampaignSendRequestSchema>

export const NotificationRecipientSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  role: z.string(),
})
export type NotificationRecipient = z.infer<typeof NotificationRecipientSchema>

export const NotificationRecipientPageSchema = z.object({
  items: z.array(NotificationRecipientSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
})
export type NotificationRecipientPage = z.infer<typeof NotificationRecipientPageSchema>

export const PostPurchaseNotificationSettingsSchema = z.object({
  version: z.literal(1),
  enabled: z.boolean(),
  delayHours: z
    .number()
    .int()
    .min(1)
    .max(24 * 30),
  content: NotificationLocalizedContentSchema,
})
export type PostPurchaseNotificationSettings = z.infer<
  typeof PostPurchaseNotificationSettingsSchema
>

export const DEFAULT_POST_PURCHASE_NOTIFICATION_SETTINGS: PostPurchaseNotificationSettings = {
  version: 1,
  enabled: true,
  delayHours: 24,
  content: {
    ru: {
      title: "Как вам покупка?",
      body: "Надеемся, с заказом всё хорошо. Если нужна помощь, откройте заказ и свяжитесь с нами.",
    },
    en: {
      title: "How is your purchase?",
      body: "We hope everything is going well. Open your order if you need any help.",
    },
    hy: {
      title: "Ինչպե՞ս է ձեր գնումը",
      body: "Հուսով ենք՝ պատվերի հետ ամեն ինչ լավ է։ Օգնության համար բացեք պատվերը։",
    },
  },
}

// --- Настройки уведомлений (Э6: fd-notif-02/14/15/20) ---

// Флаги включения по каждой из шести категорий.
export const NotificationCategoryTogglesSchema = z.object({
  system: z.boolean(),
  trade: z.boolean(),
  marketing: z.boolean(),
  social: z.boolean(),
  financial: z.boolean(),
  analytics: z.boolean(),
  // §9: тумблер категории «Мне нравится» (добавлен аддитивно).
  likes: z.boolean(),
})
export type NotificationCategoryToggles = z.infer<typeof NotificationCategoryTogglesSchema>

export const NotificationMarketingFrequencySchema = z.enum(["immediate", "daily", "weekly", "off"])
export type NotificationMarketingFrequency = z.infer<typeof NotificationMarketingFrequencySchema>

// Персональные сигналы уведомлений v2 (fd-notif-01/06/07/12). Отдельные тумблеры
// поверх шести категорий: снова-в-наличии, снижение цены на избранное и
// еженедельный дайджест. Дайджест — строго opt-in (по умолчанию выключен), чтобы
// после деплоя ничего не рассылалось само собой.
export const NotificationSignalTogglesSchema = z.object({
  backInStock: z.boolean(),
  priceDrop: z.boolean(),
  digest: z.boolean(),
})
export type NotificationSignalToggles = z.infer<typeof NotificationSignalTogglesSchema>

// Канал «почта» (26.09.2026). enabled — служебные письма (модерация товара,
// доставка заказа), marketing — рекламные (снижение цены, снова в наличии,
// всегда со ссылкой отписки). Критичные письма — коды, заказ оформлен/оплачен,
// отправлен, выплаты, итог модерации магазина — этими флагами не выключаются.
export const NotificationEmailChannelSchema = z.object({
  enabled: z.boolean(),
  marketing: z.boolean(),
})
export type NotificationEmailChannel = z.infer<typeof NotificationEmailChannelSchema>

// Полный снимок настроек пользователя — ответ GET /notifications/preferences.
export const NotificationPreferencesSchema = z.object({
  categories: NotificationCategoryTogglesSchema,
  marketingFrequency: NotificationMarketingFrequencySchema,
  // Магазины, от которых пользователь отписался (fd-notif-15).
  mutedShopIds: z.array(z.string().min(1)),
  // Персональные сигналы v2 (fd-notif-01/06/07/12).
  signals: NotificationSignalTogglesSchema,
  // Канал «почта». Необязателен в ответе ради старых клиентов и моков.
  email: NotificationEmailChannelSchema.optional(),
  // Категории товаров, от которых пользователь отписался (fd-notif-15): товарные
  // уведомления (цена, наличие, подборки) по ним не приходят. Необязательно в
  // ответе ради старых клиентов и моков.
  mutedCategoryIds: z.array(z.string().min(1)).optional(),
})
export type NotificationPreferences = z.infer<typeof NotificationPreferencesSchema>

// Частичное обновление — тело PUT /notifications/preferences. Любое поле
// опционально: не переданное остаётся прежним; mutedShopIds заменяет список
// отписок целиком.
export const NotificationPreferencesUpdateSchema = z
  .object({
    categories: NotificationCategoryTogglesSchema.partial(),
    marketingFrequency: NotificationMarketingFrequencySchema,
    mutedShopIds: z.array(z.string().min(1).max(120)).max(1000),
    // Заменяет список отписок от категорий целиком (fd-notif-15).
    mutedCategoryIds: z.array(z.string().min(1).max(120)).max(500),
    signals: NotificationSignalTogglesSchema.partial(),
    email: NotificationEmailChannelSchema.partial(),
  })
  .partial()
export type NotificationPreferencesUpdate = z.infer<typeof NotificationPreferencesUpdateSchema>

export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  categories: {
    system: true,
    trade: true,
    marketing: true,
    social: true,
    financial: true,
    analytics: true,
    likes: true,
  },
  marketingFrequency: "immediate",
  mutedShopIds: [],
  signals: {
    // Снова-в-наличии и снижение цены — явные действия покупателя (подписка на
    // товар / добавление в избранное), поэтому по умолчанию включены.
    backInStock: true,
    priceDrop: true,
    // Дайджест — opt-in: пока покупатель сам не включит, рассылки нет.
    digest: false,
  },
}

// --- §9: плоские настройки уведомлений по категориям ---
//
// Ответ GET /account/notification-settings и тело PUT (частичное). Это плоский
// Record<NotificationCategory, boolean> — по флагу на каждую категорию. По сути
// та же карта, что и NotificationPreferences.categories, только отдельным
// самодостаточным контрактом для экрана настроек мобильного/веб-клиента.
// Дефолт — все категории включены.
export const NotificationSettingsSchema = NotificationCategoryTogglesSchema
export type NotificationSettings = NotificationCategoryToggles

export const NotificationSettingsUpdateSchema = NotificationCategoryTogglesSchema.partial()
export type NotificationSettingsUpdate = z.infer<typeof NotificationSettingsUpdateSchema>

export const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings =
  DEFAULT_NOTIFICATION_PREFERENCES.categories

// --- Список ожидания «снова в наличии» (fd-notif-01) ---

// Элемент списка ожидания покупателя — ответ GET /buyer/waitlist. Несёт краткую
// карточку товара для показа в списке подписок.
export const WaitlistItemSchema = z.object({
  productId: z.string().min(1),
  name: z.string(),
  photo: z.string().nullable(),
  price: z.number().nonnegative(),
  inStock: z.boolean(),
  createdAt: z.string().datetime(),
  notifiedAt: z.string().datetime().nullable(),
})
export type WaitlistItem = z.infer<typeof WaitlistItemSchema>

export const WaitlistListSchema = z.object({
  items: z.array(WaitlistItemSchema),
})
export type WaitlistList = z.infer<typeof WaitlistListSchema>

// --- Еженедельный дайджест / подборки (fd-notif-07/12) ---

// Конфигурация каденции дайджеста (ключ-значение в settings). По умолчанию
// ВЫКЛЮЧЕНО и раз в 7 дней — безопасный дефолт: после деплоя дайджест молчит,
// пока его явно не включат.
export const WeeklyDigestSettingsSchema = z.object({
  version: z.literal(1),
  enabled: z.boolean(),
  cadenceDays: z.number().int().min(1).max(90),
})
export type WeeklyDigestSettings = z.infer<typeof WeeklyDigestSettingsSchema>

export const DEFAULT_WEEKLY_DIGEST_SETTINGS: WeeklyDigestSettings = {
  version: 1,
  enabled: false,
  cadenceDays: 7,
}

// --- Темы уведомлений ЛК покупателя (веб-макет «Профиль», 10.09.2026) ---
//
// Макет группирует ленту и настройки не по шести категориям кампаний, а по
// темам покупателя: Корзина, Доставки, Заказы, Возвраты, Мне нравится, Любимые
// магазины, Отзывы, Способ оплаты/Оплата, Поддержка. Тема — производная от
// `kind` (+ статус для order_status), в БД не хранится; правило одно на сервере
// (notificationTopicSql) и клиенте (notificationTopicOfKind).
export const NotificationTopicSchema = z.enum([
  "cart",
  "delivery",
  "orders",
  "returns",
  "likes",
  "shops",
  "reviews",
  "payment",
  "support",
])
export type NotificationTopic = z.infer<typeof NotificationTopicSchema>

// Статусы заказа, которые относятся к теме «Доставки», а не «Заказы».
export const NOTIFICATION_DELIVERY_STATUSES = ["ready_to_ship", "shipped", "in_transit", "delivered"] as const

export const notificationTopicOfKind = (
  kind: NotificationKind,
  data?: Record<string, unknown>,
): NotificationTopic | null => {
  switch (kind) {
    case "order_status":
      return (NOTIFICATION_DELIVERY_STATUSES as readonly string[]).includes(String(data?.status ?? ""))
        ? "delivery"
        : "orders"
    case "post_purchase_follow_up":
      return "orders"
    case "cart_price_drop":
      return "cart"
    case "purchase_receipt":
      return "payment"
    case "return_status":
      return "returns"
    case "price_drop":
    case "waitlist_restock":
      return "likes"
    case "weekly_picks":
      return "shops"
    case "review_activity":
    case "review_reply":
    case "product_question":
    case "product_answer":
      return "reviews"
    case "support_reply":
      return "support"
    default:
      return null
  }
}

// Флаги тем (true — тема включена). Ответ GET/PUT /notifications/topic-settings.
export const NotificationTopicSettingsSchema = z.object(
  Object.fromEntries(NotificationTopicSchema.options.map((t) => [t, z.boolean()])) as Record<
    NotificationTopic,
    z.ZodBoolean
  >,
)
export type NotificationTopicSettings = z.infer<typeof NotificationTopicSettingsSchema>
export const NotificationTopicSettingsUpdateSchema = NotificationTopicSettingsSchema.partial()
export type NotificationTopicSettingsUpdate = z.infer<typeof NotificationTopicSettingsUpdateSchema>

// Сколько видимых (не скрытых) уведомлений в каждой теме — для «Показать N».
export const NotificationTopicCountsSchema = z.object({
  total: z.number().int().nonnegative(),
  topics: z.record(NotificationTopicSchema, z.number().int().nonnegative()),
})
export type NotificationTopicCounts = z.infer<typeof NotificationTopicCountsSchema>

// --- Telegram (fd-platform-04) ---
//
// Бот площадки: токен в окружении шлюза (TELEGRAM_BOT_TOKEN). Без токена
// enabled = false и клиенты блок не показывают. Привязка — ссылкой
// t.me/<бот>?start=<одноразовый код>: пользователь нажимает «Старт», бот
// присылает код на вебхук шлюза, шлюз привязывает чат к аккаунту.
export const TelegramLinkStatusSchema = z.object({
  enabled: z.boolean(),
  botUsername: z.string(),
  linked: z.boolean(),
  /** @username в Telegram, если есть. */
  username: z.string().nullable().optional(),
  linkedAt: z.string().datetime().nullable().optional(),
})
export type TelegramLinkStatus = z.infer<typeof TelegramLinkStatusSchema>

export const TelegramLinkStartSchema = z.object({
  url: z.string().url(),
  expiresAt: z.string().datetime(),
})
export type TelegramLinkStart = z.infer<typeof TelegramLinkStartSchema>

// Приложение, из которого привязывают бот: витрина или кабинет. Ссылка кнопки
// «Открыть» в сообщении ведёт туда же, откуда пришло уведомление.
export const TelegramLinkRequestSchema = z.object({
  app: z.enum(["buyer", "seller"]).default("buyer"),
})
export type TelegramLinkRequest = z.infer<typeof TelegramLinkRequestSchema>
